import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./Kassa.css";
import api from "../api";
import { getWarehouseAccess } from "../auth";
import { useSklad } from "../SkladContext";

/* -------- Formatters -------- */
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("uz-UZ", { maximumFractionDigits: 0 });
const toInputAmount = (n) => {
  const x = Number(n || 0);
  return x ? String(x) : "";
};

/* -------- Parsers -------- */
const parseAmount = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/\s/g, "").replace(/,/g, ".");
  const x = Number(s);
  return Number.isFinite(x) ? x : 0;
};

const getApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === "string") return data || fallback;
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
  if (data && typeof data === "object") {
    for (const value of Object.values(data)) {
      if (typeof value === "string" && value.trim()) return value;
      if (Array.isArray(value) && typeof value[0] === "string" && value[0].trim()) return value[0];
    }
  }
  return fallback;
};

/* -------- Sana util -------- */
const ymd = (d) => {
  const Y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, "0");
  const D = String(d.getDate()).padStart(2, "0");
  return `${Y}-${M}-${D}`;
};
const inRange = (dateStr, fromStr, toStr) => {
  if (!dateStr) return false;
  if (fromStr && dateStr < fromStr) return false;
  if (toStr && dateStr > toStr) return false;
  return true;
};

/* -------- Nom normalize -------- */
const normalizeNom = (s) => String(s || "").trim().replace(/\s+/g, " ");
const sellerLabel = (s) => {
  if (!s) return "";
  const nm = (s.ismi || s.name || "").trim();
  return nm;
};
const matchSellerByName = (name, list) => {
  const key = normalizeNom(name).toLowerCase();
  if (!key) return null;
  const matches = (list || []).filter((s) => {
    const ismi = normalizeNom(s?.ismi || "").toLowerCase();
    return key === ismi;
  });
  if (matches.length === 1) return matches[0];
  return null;
};

/* ============================================================
   NomAutocomplete (yozish + qidirish + tanlash)
============================================================ */
function NomAutocomplete({
  value,
  onChange,
  options,
  onPickItem,
  placeholder,
  disabled,
  minChars = 0,
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const q = normalizeNom(value).toLowerCase();
  const getLabel = (item) => (typeof item === "string" ? item : item?.label || "");
  const getKey = (item) => {
    if (typeof item === "string") return item;
    const base = item?.id ?? item?.value ?? getLabel(item);
    return `${item?.type || "opt"}-${base}`;
  };
  const getSub = (item) => (typeof item === "string" ? "" : item?.sub || "");

  const filtered = useMemo(() => {
    const list = Array.isArray(options) ? options : [];
    if (!q) return list.slice(0, 12);
    if (q.length < minChars) return [];
    return list
      .filter((x) => String(getLabel(x)).toLowerCase().includes(q))
      .slice(0, 12);
  }, [options, q, minChars]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) {
        setOpen(false);
        setActive(-1);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (item) => {
    const name = getLabel(item);
    onChange(normalizeNom(name));
    if (onPickItem) onPickItem(item);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((p) => Math.min(p + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter") {
      if (active >= 0 && filtered[active]) {
        e.preventDefault();
        pick(filtered[active]);
      }
    }
  };

  return (
    <div className="nom-ac" ref={wrapRef}>
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
      />

      {open && filtered.length > 0 && (
        <div className="nom-ac-dd">
          {filtered.map((item, idx) => {
            const label = getLabel(item);
            const sub = getSub(item);
            const isSeller = typeof item !== "string" && item?.type === "seller";
            return (
            <button
              type="button"
              key={getKey(item)}
              className={"nom-ac-item " + (idx === active ? "active" : "")}
              onMouseEnter={() => setActive(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(item)}
            >
              <span className="nom-ac-main">{label}</span>
              {sub && <span className="nom-ac-sub">{sub}</span>}
              {isSeller && <span className="nom-ac-badge">Mijoz</span>}
            </button>
          )})}
        </div>
      )}
    </div>
  );
}

export default function Kassa() {
  const warehouseAccess = useMemo(() => getWarehouseAccess(), []);
  const { activeSklad } = useSklad();

  const effectiveSkladId = useMemo(() => {
    if (!warehouseAccess.allAccess) {
      return warehouseAccess.allowedIds.length === 1
        ? warehouseAccess.allowedIds[0]
        : null;
    }
    return activeSklad && activeSklad !== "all" ? Number(activeSklad) : null;
  }, [warehouseAccess, activeSklad]);

  const [balance, setBalance] = useState({ cash_som: 0, card_som: 0, terminal_som: 0 });
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);

  // Modals
  const [openOut, setOpenOut] = useState(false);
  const [openEmpty, setOpenEmpty] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Expense form
  const [outNom, setOutNom] = useState("");
  const [outReason, setOutReason] = useState("");
  const [outUzsCash, setOutUzsCash] = useState("");
  const [outUzsCard, setOutUzsCard] = useState("");
  const [outTerminalSom, setOutTerminalSom] = useState("");
  const [outSeller, setOutSeller] = useState(null);
  const [sellerOptions, setSellerOptions] = useState([]);
  const sellerSearchTimer = useRef(null);

  // Reset form
  const [emptyReason, setEmptyReason] = useState("");

  // Mobile panel tab
  const [mobilePanel, setMobilePanel] = useState("income"); // 'income' | 'expense'

  // Loading states
  const [loading, setLoading] = useState(false);
  const [savingOut, setSavingOut] = useState(false);
  const [reseting, setReseting] = useState(false);

  // Date filters
  const today = ymd(new Date());
  const [incFrom, setIncFrom] = useState(() => today);
  const [incTo, setIncTo] = useState(() => today);
  const [incBuyerQ, setIncBuyerQ] = useState("");
  const [incNoteQ, setIncNoteQ] = useState("");

  const [expFrom, setExpFrom] = useState(() => today);
  const [expTo, setExpTo] = useState(() => today);

  // Expense nom filter
  const [expNomQ, setExpNomQ] = useState("");

  /* ------- Fetch ------- */
  const fetchBalance = useCallback(async (skladId) => {
    const params = skladId ? { sklad: skladId } : {};
    const { data } = await api.get("kassa/", { params });
    setBalance({
      cash_som: Number(data?.cash_som ?? 0),
      card_som: Number(data?.card_som ?? 0),
      terminal_som: Number(data?.terminal_som ?? data?.dollar ?? 0),
    });
  }, []);

  const fetchHistory = useCallback(async (skladId) => {
    const params = skladId ? { sklad: skladId } : {};
    const { data } = await api.get("kassa/history/", { params });
    const list = Array.isArray(data) ? data : [];
    setIncomes(list.filter((x) => x._type === "income"));
    setExpenses(list.filter((x) => x._type === "withdraw"));
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        await Promise.all([fetchBalance(effectiveSkladId), fetchHistory(effectiveSkladId)]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [fetchBalance, fetchHistory, effectiveSkladId]);

  /* ------- Seller search (chiqim uchun) ------- */
  useEffect(() => {
    if (!openOut) {
      setSellerOptions([]);
      return;
    }
    const q = normalizeNom(outNom);
    if (!q || q.length < 2) {
      setSellerOptions([]);
      return;
    }
    clearTimeout(sellerSearchTimer.current);
    sellerSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("sotuvchi/", { params: { search: q } });
        const list = (data?.results || data || []).map((s) => ({
          id: s.id,
          ismi: s.ismi,
          Dokonnomi: s.Dokonnomi,
          phone: s.telraqam1 || s.telraqam2 || "",
        }));
        setSellerOptions(list);
      } catch (e) {
        console.log("Seller search error", e?.response?.status, e?.response?.data);
        setSellerOptions([]);
      }
    }, 300);
    return () => clearTimeout(sellerSearchTimer.current);
  }, [outNom, openOut]);

  /* ------- Auto match seller if exact name ------- */
  useEffect(() => {
    if (!openOut) return;
    if (outSeller) return;
    const auto = matchSellerByName(outNom, sellerOptions);
    if (auto) {
      setOutSeller({ type: "seller", id: auto.id, label: sellerLabel(auto) });
    }
  }, [outNom, sellerOptions, outSeller, openOut]);

  /* ------- Nom options (expenses ichidan unique) ------- */
  const nomOptions = useMemo(() => {
    const map = new Map(); // lower -> original
    for (const r of expenses) {
      if (r?.is_seller || r?.sotuvchi_id) continue;
      const nm = normalizeNom(r?.nom);
      if (!nm) continue;
      const k = nm.toLowerCase();
      if (!map.has(k)) map.set(k, nm);
    }
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, "uz"));
  }, [expenses]);

  const sellerOptionItems = useMemo(() => {
    return (sellerOptions || [])
      .map((s) => {
        const label = sellerLabel(s);
        if (!label) return null;
        return {
          type: "seller",
          id: s.id,
          label,
          sub: "",
        };
      })
      .filter(Boolean);
  }, [sellerOptions]);

  const outNomOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const it of sellerOptionItems) {
      const key = `seller-${it.id}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(it);
      }
    }
    for (const nm of nomOptions) {
      const key = `nom-${nm.toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ type: "nom", label: nm });
      }
    }
    return out;
  }, [sellerOptionItems, nomOptions]);

  /* ------- Filtered lists ------- */
  const incomesFiltered = useMemo(() => {
    let list = incomes;
    if (incFrom || incTo) {
      list = list.filter((r) => inRange(r.date, incFrom, incTo));
    }
    const buyerQ = normalizeNom(incBuyerQ).toLowerCase();
    if (buyerQ) {
      list = list.filter((r) =>
        String(r.buyer_name || "").toLowerCase().includes(buyerQ)
      );
    }
    const noteQ = normalizeNom(incNoteQ).toLowerCase();
    if (noteQ) {
      list = list.filter((r) =>
        String(r.note || r.izoh || r.comment || "").toLowerCase().includes(noteQ)
      );
    }
    return list;
  }, [incomes, incFrom, incTo, incBuyerQ, incNoteQ]);

  const expensesFiltered = useMemo(() => {
    let list = expenses;

    if (expFrom || expTo) {
      list = list.filter((r) => inRange(r.date, expFrom, expTo));
    }

    const q = normalizeNom(expNomQ).toLowerCase();
    if (q) {
      list = list.filter((r) => String(r.nom || "").toLowerCase().includes(q));
    }

    return list;
  }, [expenses, expFrom, expTo, expNomQ]);

  /* ------- Incomes grouped (buyer + date + time + note) ------- */
  const incomesGrouped = useMemo(() => {
    const map = new Map();
    for (const r of incomesFiltered) {
      const buyer = r.buyer_name || "—";
      const note = r.note || r.izoh || r.comment || "";
      const key = `${buyer}__${r.date}__${r.time}__${note}`;
      if (!map.has(key)) {
        map.set(key, {
          _key: key,
          buyer_name: buyer,
          note,
          date: r.date,
          time: r.time,
          terminal_som: Number((r.terminal_som ?? r.dollar) || 0),
          cash_som: Number(r.cash_som || 0),
          card_som: Number(r.card_som || 0),
        });
      } else {
        const it = map.get(key);
        it.terminal_som += Number((r.terminal_som ?? r.dollar) || 0);
        it.cash_som += Number(r.cash_som || 0);
        it.card_som += Number(r.card_som || 0);
      }
    }

    // yangi -> eski tartib (oxirgilar tepada bo‘lsin)
    return Array.from(map.values()).sort((a, b) => {
      if (a.date === b.date) return b.time.localeCompare(a.time);
      return b.date.localeCompare(a.date);
    });
  }, [incomesFiltered]);

  /* ------- Totals ------- */
  const totalIncomeTerminal = useMemo(
    () => incomesFiltered.reduce((t, r) => t + ((r.terminal_som ?? r.dollar) || 0), 0),
    [incomesFiltered]
  );
  const totalIncomeUzs = useMemo(
    () => incomesFiltered.reduce((t, r) => t + (r.cash_som || 0), 0),
    [incomesFiltered]
  );
  const totalIncomeCard = useMemo(
    () => incomesFiltered.reduce((t, r) => t + (r.card_som || 0), 0),
    [incomesFiltered]
  );

  const totalExpenseTerminal = useMemo(
    () => expensesFiltered.reduce((t, r) => t + ((r.terminal_som ?? r.dollar) || 0), 0),
    [expensesFiltered]
  );
  const totalExpenseUzs = useMemo(
    () => expensesFiltered.reduce((t, r) => t + (r.cash_som || 0), 0),
    [expensesFiltered]
  );
  const totalExpenseCard = useMemo(
    () => expensesFiltered.reduce((t, r) => t + (r.card_som || 0), 0),
    [expensesFiltered]
  );

  const onOutNomChange = useCallback(
    (val) => {
      setOutNom(val);
      if (outSeller && normalizeNom(val).toLowerCase() !== normalizeNom(outSeller.label).toLowerCase()) {
        setOutSeller(null);
      }
    },
    [outSeller]
  );

  const onOutNomPick = useCallback((item) => {
    if (item && typeof item === "object" && item.type === "seller") {
      setOutSeller(item);
    } else {
      setOutSeller(null);
    }
  }, []);

  const resetOutForm = useCallback(() => {
    setOutNom("");
    setOutReason("");
    setOutUzsCash("");
    setOutUzsCard("");
    setOutTerminalSom("");
    setOutSeller(null);
    setSellerOptions([]);
  }, []);

  const openNewExpenseModal = useCallback(() => {
    setEditingExpense(null);
    resetOutForm();
    setOpenOut(true);
  }, [resetOutForm]);

  const openEditExpenseModal = useCallback((row) => {
    if (!row?.can_edit || !row?.tx_id) return;

    setEditingExpense(row);
    setOutNom(row.nom || row.sotuvchi_name || "");
    setOutReason(row.reason || "");
    setOutUzsCash(toInputAmount(row.cash_som));
    setOutUzsCard(toInputAmount(row.card_som));
    setOutTerminalSom(toInputAmount(row.terminal_som ?? row.dollar));
    setOutSeller(
      row.sotuvchi_id
        ? {
            type: "seller",
            id: row.sotuvchi_id,
            label: row.sotuvchi_name || row.nom || "",
          }
        : null
    );
    setSellerOptions([]);
    setOpenOut(true);
  }, []);

  const closeOutModal = useCallback(() => {
    if (savingOut) return;
    setOpenOut(false);
    setEditingExpense(null);
    resetOutForm();
  }, [resetOutForm, savingOut]);

  const isEditingExpense = Boolean(editingExpense?.tx_id);

  /* ------- Actions ------- */
  const submitExpense = useCallback(async () => {
    if (savingOut) return;

    const reason = normalizeNom(outReason);
    if (!reason) return alert("Sabab shart!");

    const nom = normalizeNom(outNom);
    if (!nom) return alert("Nom shart!");

    let selectedSeller = outSeller;
    if (!selectedSeller) {
      const auto = matchSellerByName(nom, sellerOptions);
      if (auto) {
        selectedSeller = { type: "seller", id: auto.id, label: sellerLabel(auto) };
        setOutSeller(selectedSeller);
      }
    }

    const payload = {
      nom,
      reason,
      cash_som: parseAmount(outUzsCash),
      card_som: parseAmount(outUzsCard),
      terminal_som: parseAmount(outTerminalSom),
    };
    if (selectedSeller?.id) payload.sotuvchi = selectedSeller.id;
    if (effectiveSkladId) payload.sklad = effectiveSkladId;

    if (payload.cash_som < 0 || payload.card_som < 0 || payload.terminal_som < 0) {
      return alert("Summalar manfiy bo’lmasligi kerak.");
    }
    if (payload.cash_som === 0 && payload.card_som === 0 && payload.terminal_som === 0) {
      return alert("Kamida bitta summa kiriting.");
    }

    try {
      setSavingOut(true);
      if (isEditingExpense) {
        await api.patch(`kassa/withdraw/${editingExpense.tx_id}/`, payload);
      } else {
        await api.post("kassa/withdraw/", payload);
      }

      resetOutForm();
      setEditingExpense(null);
      setOpenOut(false);

      await Promise.all([fetchBalance(effectiveSkladId), fetchHistory(effectiveSkladId)]);
    } catch (err) {
      console.error(err);
      alert(getApiErrorMessage(err, "Xato!"));
    } finally {
      setSavingOut(false);
    }
  }, [editingExpense, effectiveSkladId, fetchBalance, fetchHistory, isEditingExpense, outReason, outSeller, outNom, outUzsCash, outUzsCard, outTerminalSom, resetOutForm, sellerOptions, savingOut]);

  const emptyCashRegister = async () => {
    if (reseting) return;

    const reason = normalizeNom(emptyReason);
    if (!reason) return alert("Sabab shart!");

    try {
      setReseting(true);
      const resetPayload = { reason };
      if (effectiveSkladId) resetPayload.sklad = effectiveSkladId;
      await api.post("kassa/reset/", resetPayload);
      setEmptyReason("");
      setOpenEmpty(false);
      await Promise.all([fetchBalance(effectiveSkladId), fetchHistory(effectiveSkladId)]);
    } catch (err) {
      console.error(err);
      alert(getApiErrorMessage(err, "Kassani bo‘shatishda xatolik!"));
    } finally {
      setReseting(false);
    }
  };

  const setTodayInc = () => {
    const t = ymd(new Date());
    setIncFrom(t);
    setIncTo(t);
  };
  const setTodayExp = () => {
    const t = ymd(new Date());
    setExpFrom(t);
    setExpTo(t);
  };

  return (
    <div className="kassa-page">
      {/* Header */}
      <div className="header fr-panel">
        <div className="header-title">Kassadagi pul</div>
        <div className="header-actions">
          <button className="btn danger" onClick={openNewExpenseModal} disabled={loading}>
            Chiqim
          </button>
          <button className="btn outline" onClick={() => setOpenEmpty(true)} disabled={loading}>
            Bo‘shatish
          </button>
        </div>
      </div>

      {/* Balances */}
      <div className="balances fr-panel">
        <div className="bal bal-usd">
          <div className="num">{fmtMoney(balance.terminal_som)} so‘m</div>
          <div className="hint">Terminal</div>
        </div>
        <div className="bal bal-uzs">
          <div className="num">{fmtMoney(balance.cash_som)} so‘m</div>
        </div>
        <div className="bal bal-card">
          <div className="num">{fmtMoney(balance.card_som)} so‘m</div>
          <div className="hint">Karta</div>
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="kassa-mob-tabs">
        <button
          className={`kassa-mob-tab${mobilePanel === "income" ? " active" : ""}`}
          onClick={() => setMobilePanel("income")}
        >
          Kirimlar
        </button>
        <button
          className={`kassa-mob-tab${mobilePanel === "expense" ? " active" : ""}`}
          onClick={() => setMobilePanel("expense")}
        >
          Chiqimlar
        </button>
      </div>

      {/* Grid */}
      <div className="grid">
        {/* KIRIM panel */}
        <div className={`panel fr-card${mobilePanel === "expense" ? " kassa-hide-mobile" : ""}`}>
          <div className="panel-title">Kirimlar</div>

          <div className="filter-row">
            <div className="filter-group">
              <label>dan</label>
              <input type="date" value={incFrom} onChange={(e) => setIncFrom(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>gacha</label>
              <input type="date" value={incTo} onChange={(e) => setIncTo(e.target.value)} />
            </div>
            <div className="filter-group grow">
              <label>Haridor</label>
              <input
                type="text"
                placeholder="Ism bo‘yicha qidirish"
                value={incBuyerQ}
                onChange={(e) => setIncBuyerQ(e.target.value)}
              />
            </div>
            <div className="filter-group grow">
              <label>Izoh</label>
              <input
                type="text"
                placeholder="Izoh bo‘yicha qidirish"
                value={incNoteQ}
                onChange={(e) => setIncNoteQ(e.target.value)}
              />
            </div>
            <button className="btn small" onClick={setTodayInc}>
              Bugun
            </button>
            <button className="btn small outline" onClick={setTodayInc}>
              Tozalash
            </button>
          </div>

          <div className="table-scroll">
            <div className="table">
              <div className="thead incomes sticky">
                <div>Haridor</div>
                <div>Izoh</div>
                <div>Sana</div>
                <div>Vaqt</div>
                <div>Naqd</div>
                <div>Karta</div>
                <div>Terminal</div>
              </div>

              <div className="tbody scrollable">
                {incomesGrouped.map((r) => (
                  <div className="trow incomes" key={r._key}>
                    <div className="cell text" data-label="Haridor">{r.buyer_name}</div>
                    <div className="cell text" data-label="Izoh">{r.note || "—"}</div>
                    <div data-label="Sana">{r.date}</div>
                    <div data-label="Vaqt">{r.time}</div>
                    <div data-label="Naqd">{fmtMoney(r.cash_som)}</div>
                    <div data-label="Karta">{fmtMoney(r.card_som)}</div>
                    <div data-label="Terminal">{fmtMoney(r.terminal_som ?? r.dollar)}</div>
                  </div>
                ))}

                {incomesGrouped.length === 0 && <div className="empty-row">Ma’lumot topilmadi</div>}
              </div>

              <div className="tfoot incomes">
                <div>Jami:</div>
                <div></div>
                <div></div>
                <div></div>
                <div>{fmtMoney(totalIncomeUzs)}</div>
                <div>{fmtMoney(totalIncomeCard)}</div>
                <div>{fmtMoney(totalIncomeTerminal)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* CHIQIM panel */}
        <div className={`panel fr-card${mobilePanel === "income" ? " kassa-hide-mobile" : ""}`}>
          <div className="panel-title">Chiqimlar</div>

          <div className="filter-row">
            <div className="filter-group">
              <label>dan</label>
              <input type="date" value={expFrom} onChange={(e) => setExpFrom(e.target.value)} />
            </div>

            <div className="filter-group">
              <label>gacha</label>
              <input type="date" value={expTo} onChange={(e) => setExpTo(e.target.value)} />
            </div>

            <div className="filter-group grow">
              <label>Nom</label>
              <NomAutocomplete
                value={expNomQ}
                onChange={setExpNomQ}
                options={nomOptions}
                placeholder="Masalan: Bobur..."
                minChars={0}
              />
            </div>

            <button className="btn small" onClick={setTodayExp}>
              Bugun
            </button>
            <button className="btn small outline" onClick={setTodayExp}>
              Tozalash
            </button>
          </div>

          <div className="table-scroll">
            <div className="table">
              <div className="thead expenses sticky">
                <div>Amal</div>
                <div>Nom</div>
                <div>Sabab</div>
                <div>Sana</div>
                <div>Vaqt</div>
                <div>Naqd</div>
                <div>So‘m</div>
                <div>Karta</div>
              </div>

              <div className="tbody scrollable">
                {expensesFiltered.map((r) => (
                  <div className="trow expenses" key={r.id}>
                    <div className="expense-action-cell" data-label="Amal">
                      {r.can_edit ? (
                        <button
                          type="button"
                          className="btn small outline expense-action-btn"
                          onClick={() => openEditExpenseModal(r)}
                        >
                          Tahrirlash
                        </button>
                      ) : (
                        <span className="expense-action-placeholder">-</span>
                      )}
                    </div>
                    <div className="cell text" data-label="Nom">{r.nom || "—"}</div>
                    <div className="cell text" data-label="Sabab">{r.reason}</div>
                    <div data-label="Sana">{r.date}</div>
                    <div data-label="Vaqt">{r.time}</div>
                    <div data-label="Naqd">{fmtMoney(r.cash_som)}</div>
                    <div data-label="Karta">{fmtMoney(r.card_som)}</div>
                    <div data-label="Terminal">{fmtMoney(r.terminal_som ?? r.dollar)}</div>
                  </div>
                ))}

                {expensesFiltered.length === 0 && <div className="empty-row">Ma’lumot topilmadi</div>}
              </div>

              <div className="tfoot expenses">
                <div></div>
                <div>Jami:</div>
                <div></div>
                <div></div>
                <div></div>
                <div>{fmtMoney(totalExpenseUzs)}</div>
                <div>{fmtMoney(totalExpenseCard)}</div>
                <div>{fmtMoney(totalExpenseTerminal)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chiqim modal */}
      {openOut && (
        <div className="modal" onClick={closeOutModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              {isEditingExpense ? "Chiqimni tahrirlash" : "Chiqim qilish"}
            </div>

            <label>Nom (majburiy):</label>
            <NomAutocomplete
              value={outNom}
              onChange={onOutNomChange}
              onPickItem={onOutNomPick}
              options={outNomOptions}
              placeholder="Masalan: Bobur"
              disabled={savingOut}
              minChars={0}
            />
            {outSeller ? (
              <div className="help">
                Mijoz tanlandi: <b>{outSeller.label}</b>. Chiqim balansdan yechiladi va Telegram xabar yuboriladi.
              </div>
            ) : (
              <div className="help">
                Ism aniq mos kelsa, mijoz avtomatik tanlanadi. Tanlanganda balansdan yechiladi va Telegram xabar boradi.
              </div>
            )}

            <label>Sabab (majburiy):</label>
            <input
              value={outReason}
              onChange={(e) => setOutReason(e.target.value)}
              disabled={savingOut}
              placeholder="Masalan: oylik berildi"
            />

            <div className="row three">
              <label>
                <span>Naqd so‘m</span>
                <input
                  value={outUzsCash}
                  onChange={(e) => setOutUzsCash(e.target.value)}
                  disabled={savingOut}
                  inputMode="decimal"
                  placeholder="0"
                />
              </label>

              <label>
                <span>Karta so‘m</span>
                <input
                  value={outUzsCard}
                  onChange={(e) => setOutUzsCard(e.target.value)}
                  disabled={savingOut}
                  inputMode="decimal"
                  placeholder="0"
                />
              </label>

              <label>
                <span>Terminal so‘m</span>
                <input
                  value={outTerminalSom}
                  onChange={(e) => setOutTerminalSom(e.target.value)}
                  disabled={savingOut}
                  inputMode="decimal"
                  placeholder="0"
                />
              </label>
            </div>

            <div className="actions">
              <button className="btn outline" onClick={closeOutModal} disabled={savingOut}>
                Bekor
              </button>
              <button className="btn primary" onClick={submitExpense} disabled={savingOut}>
                {savingOut ? "Saqlanmoqda..." : isEditingExpense ? "Yangilash" : "Saqlash"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset modal */}
      {openEmpty && (
        <div className="modal" onClick={() => !reseting && setOpenEmpty(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Kassani bo‘shatish</div>

            <label>Sabab (majburiy):</label>
            <input
              value={emptyReason}
              onChange={(e) => setEmptyReason(e.target.value)}
              disabled={reseting}
              placeholder="Masalan: kassani topshirish"
            />

            <div className="actions">
              <button className="btn outline" onClick={() => setOpenEmpty(false)} disabled={reseting}>
                Bekor
              </button>
              <button className="btn danger" onClick={emptyCashRegister} disabled={reseting}>
                {reseting ? "Bo‘shatilmoqda..." : "Bo‘shatish"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

